import {LitElement, html, css} from 'lit';
import * as XLSX from 'xlsx';
import {clamBackend, type GafeteRow, type GafeteImportResult} from '../../services/clam-backend.service.js';

interface ParsedRow {
	nombre: string;
	email: string;
	gafete: string;
}

/** Quita acentos/diacríticos y pasa a minúsculas para comparación de headers. */
function fold(s: string): string {
	return (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
}

/** Toma de un objeto-fila el primer valor cuya key contiene algún candidato. */
function pickField(row: Record<string, unknown>, candidates: string[]): string {
	const keys = Object.keys(row);
	for (const cand of candidates) {
		const c = fold(cand);
		for (const k of keys) {
			if (fold(k).includes(c)) {
				const v = row[k];
				if (v !== null && v !== undefined && String(v).trim() !== '') return String(v).trim();
			}
		}
	}
	return '';
}

/**
 * Cargador de gafetes (RFID/credencial). Sube un Excel/CSV (.xlsx/.xls/.csv) con
 * columnas `nombre`, `email`, `gafete`, hace preview y los asigna a las personas
 * existentes (match por nombre o email). Feature independiente del sync de Forms.
 */
export class ClamGafeteLoader extends LitElement {
	static styles = css`
		:host { display: block; padding: var(--clam-space-6); max-width: 900px; margin: 0 auto; font-family: var(--clam-font); color: var(--clam-text); }
		h2 { color: var(--clam-text); margin: 0 0 var(--clam-space-2); font-size: var(--clam-text-2xl); }
		p.desc { color: var(--clam-text-muted); font-size: var(--clam-text-base); margin: 0 0 var(--clam-space-6); line-height: 1.5; }
		.dropzone {
			border: 2px dashed var(--clam-border); border-radius: var(--clam-radius-md);
			padding: var(--clam-space-6); text-align: center; cursor: pointer;
			background: var(--clam-surface-soft); transition: border-color .15s, background .15s;
			outline: none; display: block;
		}
		.dropzone:focus { outline: none; }
		.dropzone:focus-visible { border-color: var(--clam-primary); box-shadow: 0 0 0 3px rgba(90,157,30,0.15); }
		.dropzone:hover, .dropzone.drag { border-color: var(--clam-primary); background: rgba(90,157,30,0.06); }
		.dropzone input { display: none; }
		.dropzone .dz-text { font-size: var(--clam-text-base); color: var(--clam-text); font-weight: 600; }
		.dropzone .dz-hint { font-size: var(--clam-text-sm); color: var(--clam-text-muted); margin-top: 4px; }
		.stats { display: flex; gap: var(--clam-space-4); margin: var(--clam-space-4) 0; flex-wrap: wrap; }
		.stat { background: var(--clam-surface-soft); border-radius: var(--clam-radius-md); padding: var(--clam-space-3) var(--clam-space-4); flex: 1 1 120px; min-width: 120px; }
		.stat-num { font-size: var(--clam-text-2xl); font-weight: 700; color: var(--clam-primary); }
		.stat-label { font-size: var(--clam-text-xs); color: var(--clam-text-muted); text-transform: uppercase; }
		.preview { margin: var(--clam-space-4) 0; max-height: 280px; overflow: auto; border: 1px solid var(--clam-border); border-radius: var(--clam-radius-md); }
		table { width: 100%; border-collapse: collapse; font-size: var(--clam-text-sm); }
		th, td { padding: 8px 10px; text-align: left; border-bottom: 1px solid var(--clam-border); }
		th { background: var(--clam-surface-soft); color: var(--clam-text-muted); font-weight: 600; position: sticky; top: 0; }
		td.ok { color: var(--clam-green-dark); }
		td.warn { color: #b8860b; }
		.btn { padding: 10px var(--clam-space-5); border: none; border-radius: var(--clam-radius-md); font-size: var(--clam-text-base); font-weight: 600; cursor: pointer; font-family: inherit; min-height: 40px; }
		.btn-primary { background: var(--clam-primary); color: var(--clam-on-primary); }
		.btn-primary:hover { background: var(--clam-primary-hover); }
		.btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
		.btn-secondary { background: var(--clam-surface-soft); color: var(--clam-primary); }
		.actions { display: flex; gap: var(--clam-space-3); margin-top: var(--clam-space-4); flex-wrap: wrap; }
		.msg { padding: var(--clam-space-3) var(--clam-space-4); border-radius: var(--clam-radius-md); font-size: var(--clam-text-base); margin-top: var(--clam-space-4); }
		.msg-ok { background: #e8f8e8; color: var(--clam-green-dark); }
		.msg-err { background: #fde8e8; color: var(--clam-danger); }
		.msg-info { background: #e8f4fd; color: var(--clam-green-dark); }
		.detail-list { margin-top: var(--clam-space-2); font-size: var(--clam-text-sm); max-height: 160px; overflow: auto; }
		.detail-list li { margin: 2px 0; }
		.spinner { display: inline-block; width: 16px; height: 16px; border: 2px solid var(--clam-border); border-top-color: var(--clam-primary); border-radius: 50%; animation: spin 0.8s linear infinite; }
		@keyframes spin { to { transform: rotate(360deg); } }
	`;

	private _rows: ParsedRow[] = [];
	private _fileName = '';
	private _busy = false;
	private _msg: {type: string; text: string} | null = null;
	private _result: GafeteImportResult | null = null;

	private async _onFile(file: File): Promise<void> {
		this._msg = null;
		this._result = null;
		try {
			const buf = await file.arrayBuffer();
			const wb = XLSX.read(buf, {type: 'array'});
			const ws = wb.Sheets[wb.SheetNames[0]];
			const json = XLSX.utils.sheet_to_json<Record<string, unknown>>(ws, {defval: ''});
			const rows: ParsedRow[] = json.map((row) => ({
				nombre: pickField(row, ['nombre completo', 'nombre', 'name', 'nombre y apellido']),
				email: pickField(row, ['correo electronico', 'email', 'correo', 'mail', 'correo electronico institucional']),
				gafete: pickField(row, ['gafete', 'badge', 'tag', 'gafete id', 'credencial', 'rfid']),
			}));
			this._rows = rows;
			this._fileName = file.name;
			this._msg = {type: 'info', text: `Archivo "${file.name}" leído: ${rows.length} filas detectadas. Revisa el preview y confirma.`};
		} catch (e) {
			this._rows = [];
			this._fileName = '';
			this._msg = {type: 'err', text: `No se pudo leer el archivo: ${e instanceof Error ? e.message : String(e)}`};
		}
		this.requestUpdate();
	}

	private _onInputChange(e: Event): void {
		const input = e.target as HTMLInputElement;
		const file = input.files?.[0];
		if (file) void this._onFile(file);
		input.value = '';
	}

	private _onDrop(e: DragEvent): void {
		e.preventDefault();
		const file = e.dataTransfer?.files?.[0];
		if (file) void this._onFile(file);
	}

	private _validRows(): ParsedRow[] {
		return this._rows.filter((r) => (r.nombre || r.email) && r.gafete);
	}

	private async _import(): Promise<void> {
		const valid = this._validRows();
		if (valid.length === 0) {
			this._msg = {type: 'err', text: 'No hay filas válidas con nombre/email y gafete.'};
			this.requestUpdate();
			return;
		}
		this._busy = true;
		this._msg = {type: 'info', text: `Asignando ${valid.length} gafetes (match por nombre/email)...`};
		this._result = null;
		this.requestUpdate();
		const payload: GafeteRow[] = valid.map((r) => ({nombre: r.nombre, email: r.email, gafete: r.gafete}));
		const res = await clamBackend.importGafetes(payload);
		if (res.ok && res.data) {
			this._result = res.data;
			const d = res.data;
			this._msg = {
				type: d.errors.length > 0 ? 'err' : 'ok',
				text: `Listo: ${d.matched} coincidencias, ${d.updated} gafetes asignados, ${d.skipped} ambiguos, ${d.notFound.length} sin coincidencia.`,
			};
		} else {
			this._msg = {type: 'err', text: res.error || 'Error al importar gafetes.'};
		}
		this._busy = false;
		this.requestUpdate();
	}

	render() {
		const valid = this._validRows();
		const r = this._result;
		return html`
			<h2>Asignar Gafetes</h2>
			<p class="desc">Sube un Excel o CSV con las columnas <b>nombre</b>, <b>email</b> y <b>gafete</b> (acepta alias como <code>name</code>, <code>correo</code>, <code>badge</code>, <code>tag</code>, <code>credencial</code>, <code>rfid</code>). El sistema hace match por <b>email</b> o por <b>nombre</b> y asigna el gafete a las personas existentes, sin perder datos (solo actualiza el gafete en lotes). Si una fila no coincide o es ambigua, se reporta sin asignar.</p>

			<label class="dropzone ${this._fileName ? '' : ''}"
				@dragover=${(e: DragEvent) => e.preventDefault()}
				@drop=${(e: DragEvent) => this._onDrop(e)}>
				<input type="file" accept=".csv,.xlsx,.xls" @change=${(e: Event) => this._onInputChange(e)} />
				<div class="dz-text">${this._fileName ? `📄 ${this._fileName}` : 'Haz clic o arrastra un archivo aquí'}</div>
				<div class="dz-hint">Formatos: .csv, .xlsx, .xls — columnas: nombre, email, gafete</div>
			</label>

			${valid.length > 0 ? html`
				<div class="stats">
					<div class="stat"><div class="stat-num">${valid.length}</div><div class="stat-label">Filas válidas</div></div>
					<div class="stat"><div class="stat-num">${this._rows.length - valid.length}</div><div class="stat-label">Sin gafete/nombre</div></div>
				</div>
				<div class="preview">
					<table>
						<thead><tr><th>Nombre</th><th>Email</th><th>Gafete</th></tr></thead>
						<tbody>
							${valid.slice(0, 100).map((row) => html`
								<tr><td>${row.nombre}</td><td>${row.email}</td><td class="ok">${row.gafete}</td></tr>
							`)}
						</tbody>
					</table>
				</div>
				${valid.length > 100 ? html`<p class="desc" style="margin-top:4px">(Mostrando 100 de ${valid.length})</p>` : ''}
				<div class="actions">
					<button class="btn btn-primary" @click=${() => this._import()} ?disabled=${this._busy}>
						${this._busy ? html`<span class="spinner"></span>` : ''} Asignar ${valid.length} gafetes
					</button>
					<button class="btn btn-secondary" @click=${() => { this._rows = []; this._fileName = ''; this._result = null; this._msg = null; this.requestUpdate(); }} ?disabled=${this._busy}>
						Limpiar
					</button>
				</div>
			` : ''}

			${this._msg ? html`<div class="msg msg-${this._msg.type}">${this._msg.text}</div>` : ''}

			${r ? html`
				${r.notFound.length > 0 ? html`
					<div class="msg msg-info" style="margin-top:8px;">
						<strong>Sin coincidencia (${r.notFound.length}):</strong>
						<ul class="detail-list">${r.notFound.slice(0, 200).map((n) => html`<li>${n}</li>`)}</ul>
					</div>` : ''}
				${r.ambiguous.length > 0 ? html`
					<div class="msg msg-err" style="margin-top:8px;">
						<strong>Ambiguos (no asignados — ${r.ambiguous.length}):</strong>
						<ul class="detail-list">${r.ambiguous.slice(0, 200).map((n) => html`<li>${n}</li>`)}</ul>
					</div>` : ''}
				${r.errors.length > 0 ? html`
					<div class="msg msg-err" style="margin-top:8px;">
						<strong>Errores:</strong>
						<ul class="detail-list">${r.errors.slice(0, 100).map((n) => html`<li>${n}</li>`)}</ul>
					</div>` : ''}
			` : ''}
		`;
	}
}

customElements.define('clam-gafete-loader', ClamGafeteLoader);
